import { useState } from 'react';
import { DataLoadError } from '@/components/DataLoadError';
import { createFileRoute } from '@tanstack/react-router';
import { INBOUND_MAPPINGS } from '@/data/mock';
import { useInboundMappings } from '@/lib/use-admin-data';
import type { SourcePlatform, EntityType } from '@/types/index';
import { PLATFORM_LABELS, ENTITY_LABELS } from '@/types/index';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/utils';
import type { Icon } from '@phosphor-icons/react';
import {
  User,
  FileText,
  Receipt,
  Money,
  Package,
  ListBullets,
  Function,
  Plus,
  Lightning,
  LinkBreak,
  DotsThreeVertical,
  ArrowRight,
} from '@phosphor-icons/react';

type IconType = Icon;

/* ------------------------------------------------------------------ */
/*  Platform tabs config                                               */
/* ------------------------------------------------------------------ */
const PLATFORMS: { key: SourcePlatform; label: string }[] = [
  { key: 'chargebee', label: 'Chargebee' },
  { key: 'recurly', label: 'Recurly' },
  { key: 'braintree', label: 'Braintree' },
  { key: 'paypal', label: 'PayPal' },
  { key: 'checkout_com', label: 'Checkout.com' },
  { key: 'vantiv', label: 'Vantiv' },
];

/* ------------------------------------------------------------------ */
/*  Stream sidebar config                                              */
/* ------------------------------------------------------------------ */
const STREAMS: { key: EntityType; icon: IconType }[] = [
  { key: 'customer', icon: User },
  { key: 'subscription', icon: FileText },
  { key: 'invoice', icon: Receipt },
  { key: 'transaction', icon: Money },
  { key: 'plan', icon: Package },
];

/* ------------------------------------------------------------------ */
/*  Transform icon helper                                              */
/* ------------------------------------------------------------------ */
function TransformBadge({ transform }: { transform: string | null }) {
  if (!transform) return <span className="text-slate-300">&mdash;</span>;

  const Icon = transform.startsWith('map_') ? ListBullets : Function;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
      <Icon size={14} weight="regular" />
      {transform}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */
function InboundMappingsPage() {
  const [activePlatform, setActivePlatform] = useState<SourcePlatform>('chargebee');
  const [activeEntity, setActiveEntity] = useState<EntityType>('customer');
  const { data: allMappings = [], isError: mappingsError } = useInboundMappings();

  const config = allMappings.find(
    (m) => m.platform === activePlatform && m.entity === activeEntity,
  );
  const mappings = config?.mappings ?? [];
  const mappedCount = mappings.filter((m) => m.status === 'mapped').length;
  const totalCount = mappings.length;
  const mappedPct = totalCount > 0 ? Math.round((mappedCount / totalCount) * 100) : 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      {mappingsError && <DataLoadError what="inbound mappings" />}
      {/* ---- Platform Tabs ---- */}
      <div className="flex gap-0 border-b border-slate-200 bg-white px-8">
        {PLATFORMS.map((p) => (
          <button
            key={p.key}
            onClick={() => {
              setActivePlatform(p.key);
              setActiveEntity('customer');
            }}
            className={cn(
              'px-5 py-3 text-sm font-semibold transition-colors -mb-px',
              activePlatform === p.key
                ? 'border-b-2 border-slate-900 text-slate-900'
                : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex flex-1 min-h-0">
        {/* ---- Left Sidebar (Streams) ---- */}
        <aside className="w-64 flex-shrink-0 bg-white border-r border-slate-200 overflow-y-auto">
          <div className="px-5 pt-6 pb-2">
            <h3 className="text-sm font-bold text-slate-900">{PLATFORM_LABELS[activePlatform]} Streams</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">Source Schema</p>
          </div>
          <nav className="px-3 pb-4 space-y-0.5">
            {STREAMS.map((s) => {
              const isActive = s.key === activeEntity;
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  onClick={() => setActiveEntity(s.key)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                    isActive
                      ? 'bg-slate-900 text-white shadow'
                      : 'text-slate-600 hover:bg-slate-100',
                  )}
                >
                  <Icon size={18} weight="regular" />
                  {ENTITY_LABELS[s.key]}
                </button>
              );
            })}
          </nav>
          <div className="px-5 pb-5">
            <button className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline">
              <Plus size={16} weight="regular" />
              Add New Stream
            </button>
          </div>
        </aside>

        {/* ---- Main Content ---- */}
        <main className="flex-1 overflow-y-auto bg-[#f8f6f6] p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-black text-slate-900">Inbound Mappings</h2>
              <p className="text-sm text-slate-500 mt-1">
                {PLATFORM_LABELS[activePlatform]} &rarr; Canonical &middot; {ENTITY_LABELS[activeEntity]} entity
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-white transition-colors">
                Export Schema
              </button>
              <button className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors flex items-center gap-1.5">
                <Lightning size={16} weight="regular" />
                Bulk Auto-Map
              </button>
            </div>
          </div>

          {/* Mappings Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mb-8">
            {mappings.length === 0 ? (
              <div className="px-8 py-16 text-center text-slate-400 text-sm">
                <LinkBreak size={32} weight="regular" className="mb-2 mx-auto" />
                No mappings configured for {PLATFORM_LABELS[activePlatform]} / {ENTITY_LABELS[activeEntity]}.
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3">Raw Source Field</th>
                      <th className="px-5 py-3">Mapped Canonical Field</th>
                      <th className="px-5 py-3">Transform Function</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {mappings.map((m) => (
                      <tr key={m.sourceField} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3">
                          <code className="font-mono text-[13px] bg-slate-100 px-2 py-0.5 rounded text-slate-800">
                            {m.sourceField}
                          </code>
                        </td>
                        <td className="px-5 py-3">
                          {m.canonicalField ? (
                            <select
                              className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 font-medium"
                              value={m.canonicalField}
                              onChange={() => {}}
                            >
                              <option>{m.canonicalField}</option>
                            </select>
                          ) : (
                            <span className="text-slate-400 italic text-xs">Not mapped</span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <TransformBadge transform={m.transform} />
                        </td>
                        <td className="px-5 py-3">
                          <StatusBadge variant={m.status} />
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                            <DotsThreeVertical size={18} weight="regular" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    <span className="font-bold text-slate-700">{mappedCount}</span> of{' '}
                    <span className="font-bold text-slate-700">{totalCount}</span> fields mapped (
                    <span className="font-bold text-slate-900 font-mono tabular-nums">{mappedPct}%</span>)
                  </span>
                  <div className="flex items-center gap-2">
                    <button className="px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50 text-slate-500 font-semibold">
                      Prev
                    </button>
                    <span className="font-semibold text-slate-700">1</span>
                    <button className="px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50 text-slate-500 font-semibold">
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Bottom 2-column grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Mapping Suggestions */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-slate-900 mb-4">Mapping Suggestions</h3>
              <div className="space-y-3">
                {[
                  {
                    source: 'deleted',
                    target: 'billing_status',
                    confidence: '87%',
                    reason: 'Boolean flag commonly maps to status lifecycle field.',
                  },
                  {
                    source: 'cf_custom_field',
                    target: 'raw_payload',
                    confidence: '62%',
                    reason: 'Custom fields with no canonical equivalent are typically stored in raw payload.',
                  },
                ].map((suggestion) => (
                  <div key={suggestion.source} className="border-l-4 border-slate-900 pl-4 py-2">
                    <div className="flex items-center gap-2 text-sm">
                      <code className="font-mono text-[12px] bg-slate-100 px-1.5 py-0.5 rounded">{suggestion.source}</code>
                      <ArrowRight size={14} weight="regular" className="text-slate-400" />
                      <code className="font-mono text-[12px] bg-slate-100 text-slate-900 px-1.5 py-0.5 rounded">{suggestion.target}</code>
                      <span className="ml-auto text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        {suggestion.confidence}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{suggestion.reason}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-slate-900 mb-4">Recent Activity</h3>
              <div className="space-y-4">
                {[
                  { time: '2 hours ago', user: 'darrel@kombatix.io', action: 'Added transform unix_to_iso to created_at field.' },
                  { time: '1 day ago', user: 'system', action: 'Auto-mapped 3 new Chargebee customer fields after schema update.' },
                  { time: '3 days ago', user: 'darrel@kombatix.io', action: 'Marked cf_custom_field as critical — requires manual review.' },
                ].map((event, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-slate-900 mt-1.5" />
                      {i < 2 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                    </div>
                    <div className="pb-1">
                      <p className="text-xs text-slate-400">{event.time} &middot; {event.user}</p>
                      <p className="text-sm text-slate-700 mt-0.5">{event.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/schema/inbound')({
  component: InboundMappingsPage,
});
